import { useCallback, useEffect, useRef, useState } from "react";
import type { AuthResponse, ChildProfile } from "@howcomet/shared";
import {
  api,
  ApiError,
  clearChildToken,
  getChildToken,
  onNeedsGrownUp,
  setChildToken,
} from "./api";
import { TabBar, TopBar, type Tab } from "./components/Chrome";
import { Toast } from "./components/Toast";
import { AskScreen } from "./screens/AskScreen";
import { GoalScreen } from "./screens/GoalScreen";
import { DeviceScreen } from "./screens/DeviceScreen";
import { ParentPortal } from "./parent/ParentPortal";
import { SkyScreen } from "./screens/SkyScreen";
import { RestingScreen } from "./screens/RestingScreen";
import { WaitingForParentScreen } from "./screens/WaitingForParentScreen";
import { GuideScreen } from "./screens/GuideScreen";
import { WelcomeScreen } from "./screens/WelcomeScreen";
import { APP_PATH, surfaceFor } from "./routing";
import { onTransaction } from "./store";

/**
 * Two surfaces, one bundle: `/app` is the child's, `/parents` is the adult's,
 * opened on their own device. They share nothing but the API — the parent's
 * credential never exists on the tablet, and the child's session never reaches
 * the portal.
 *
 * `/` belongs to neither. It is reserved for the marketing site, which nginx
 * serves and this bundle knows nothing about. The decision lives in
 * `routing.ts` because the case that matters most cannot be run here: inside
 * the iPad app the pathname is `/`, and routing on the path alone would have
 * every child's tablet open a marketing page.
 */
export default function App() {
  const surface = surfaceFor(window.location);
  if (surface === "parent") return <ParentPortal />;
  if (surface === "redirect-to-app") {
    /*
      Only reachable from the dev server, which answers every path with
      index.html. `replace` rather than `href` so the back button does not
      bounce a child between here and the app.
    */
    window.location.replace(APP_PATH + window.location.search);
    return null;
  }
  return <KidApp />;
}

function KidApp() {
  const [profile, setProfile] = useState<ChildProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("ask");
  const [parentMode, setParentMode] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  /** Signed in, but the server is out of reach — not the same as signed out. */
  const [unreachable, setUnreachable] = useState(false);
  /*
    An account this device may no longer use, and the server's own words for
    why. Only suspension produces a 403 from `/api/me` — it is behind
    `requireChild` alone — so this is never a guess about what went wrong.
  */
  const [blocked, setBlocked] = useState<string | null>(null);
  /*
    The family's subscription has lapsed, in the server's own words.

    Distinct from `blocked` because the answer is different: suspension is a
    grown-up's decision and paying does not undo it, while this is a card that
    expired and the way out is one screen away. It never comes from `/api/me` —
    that route is not gated — so it arrives from whichever route the child
    happened to touch. See `onNeedsGrownUp`.
  */
  const [resting, setResting] = useState<string | null>(null);
  /*
    Dismissing the guide has to stick locally, not just optimistically on the
    profile. Switching tabs calls refresh(), which re-reads the profile from the
    server — and if the "I've seen it" write has not landed yet, the tour
    reappears on top of whatever the child was doing.
  */
  const [guideDismissed, setGuideDismissed] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // A child token lasts two months. Making a seven-year-old re-enter a pin
  // every day is a reason to stop opening the app.
  useEffect(() => {
    if (!getChildToken()) {
      setLoading(false);
      return;
    }
    loadMe();
  }, []);

  /*
    Being unable to reach the server is not being signed out.

    Without this the app fell through to the sign-up screen whenever the request
    failed — so a child who took the iPad off the home wifi was shown "I'm new
    here" and would reasonably try to register again. Only a 401 means the token
    is actually dead; everything else means try later.
  */
  const loadMe = () => {
    setLoading(true);
    api
      .me()
      .then((next) => {
        setProfile(next);
        setUnreachable(false);
        setBlocked(null);
      })
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) clearChildToken();
        /*
          A paused account is not a broken network, and saying "check the wifi"
          sends a child to go and fix something that is not wrong. The server
          already words this carefully — `requireChild` answers 403 before it
          checks the token epoch, precisely so the first thing they are told is
          the truth — and this used to throw that away and show the wifi screen
          instead, with no way off it.
        */
        else if (error instanceof ApiError && error.status === 403) setBlocked(error.message);
        else if (getChildToken()) setUnreachable(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const refresh = useCallback(() => {
    api.me().then(setProfile).catch(() => {});
  }, []);

  /*
    Any gated route saying the subscription has lapsed puts the whole app on the
    resting screen, not just the tab that asked. Registered once, for the life
    of the session, because the route that finds out is not knowable in advance.
  */
  useEffect(() => {
    onNeedsGrownUp(setResting);
  }, []);

  /**
   * Leaves the resting screen and lets the app find out for itself.
   *
   * There is nothing to check here: entitlement is not on the profile, and the
   * client is not allowed to decide it anyway. Clearing the state remounts the
   * three screens, which re-ask — and if the family is still not entitled the
   * next 403 puts this straight back. The truth stays server-side; this only
   * stops us insisting on an answer we were given a minute ago.
   */
  const wakeUp = useCallback(() => {
    setResting(null);
    refresh();
  }, [refresh]);

  /*
    Transactions StoreKit hands us without anybody pressing a button: a renewal,
    a refund, a purchase finished on another device, or an Ask to Buy the second
    grown-up approved minutes after it was asked for.

    Registered here rather than in the paywall because the paywall is behind a
    password almost nobody has open at the moment one of these lands. Without
    it the child sits on "Howcomet is having a rest" until somebody thinks to
    press Restore — which was exactly the case `onTransaction` was written for,
    and it sat unused, so that is exactly what happened.

    The device still only reports; the server decides what the signature is
    worth. Failures are swallowed on purpose — Apple's own notification is the
    durable path, and this is the fast one.
  */
  const listening = useRef(false);
  useEffect(() => {
    if (listening.current) return;
    listening.current = true;
    onTransaction((jws) => {
      void api
        .reportPurchase(jws)
        .then(() => wakeUp())
        .catch(() => {});
    });
  }, [wakeUp]);

  const onAwarded = useCallback(
    (message: string) => {
      showToast(message);
      refresh();
    },
    [showToast, refresh],
  );

  const signedIn = (auth: AuthResponse) => {
    setChildToken(auth.token);
    setProfile(auth.child);
  };

  /**
   * Drops the session and returns to the welcome screen. The remembered handle
   * is deliberately left behind, so signing back in on this device is just the
   * pin — signing out is for handing the tablet to a sibling, not for erasing
   * yourself from it.
   */
  const signOut = useCallback(() => {
    clearChildToken();
    setProfile(null);
    setParentMode(false);
    setTab("ask");
    /*
      Both dead-end screens gate on their own state and are checked before the
      welcome screen, so leaving these set means signing out clears the token
      and changes nothing on screen — a way out that visibly does nothing, which
      is worse than not offering one.
    */
    setBlocked(null);
    setUnreachable(false);
    setResting(null);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="twinkle text-[44px]">✨</div>
      </div>
    );
  }

  /*
    Turning the web view's scroll view off applies to every screen, not just the
    kid shell — so anything that used to rely on the document scrolling needs
    its own scroller or it simply cannot be reached. Sign-up is the tall one,
    and it is the very first thing a new child sees.
  */
  const scrollable = (node: React.ReactNode) => (
    <div className="h-dvh overflow-hidden">
      <div className="h-full overflow-y-auto overscroll-contain">{node}</div>
    </div>
  );

  /*
    Both of the screens below are places a child can arrive and be stuck, so
    both carry the same way out. Signing out keeps the remembered handle, so for
    the ordinary case — wrong account on a shared tablet — it costs a pin and
    nothing else.
  */
  const escapeHatch = (
    <button
      onClick={signOut}
      className="kidbtn mt-4 rounded-full border border-cream/25 px-6 py-3 text-[15px] font-bold text-cream/70"
    >
      Sign in as someone else
    </button>
  );

  if (blocked) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center px-6 text-center">
        <div className="rise max-w-sm">
          <div className="text-[64px]">🌙</div>
          <p className="disp mt-4 text-[26px] leading-tight text-cream">This sky is resting</p>
          {/* The server's words, not ours. It knows why; we only know it said no. */}
          <p className="mt-2 text-[17px] leading-relaxed text-cream/65">{blocked}</p>
          {escapeHatch}
        </div>
      </div>
    );
  }

  if (unreachable) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center px-6 text-center">
        <div className="rise max-w-sm">
          <div className="text-[64px]">🌙</div>
          <p className="disp mt-4 text-[26px] leading-tight text-cream">
            I can't reach your stars
          </p>
          <p className="mt-2 text-[17px] leading-relaxed text-cream/65">
            Check the wifi and try again.
          </p>
          <button
            onClick={loadMe}
            className="kidbtn disp mt-7 rounded-full bg-gold px-8 py-4 text-[20px] text-night"
          >
            Try again
          </button>
          <div>{escapeHatch}</div>
        </div>
      </div>
    );
  }

  if (!profile) return scrollable(<WelcomeScreen onSignedIn={signedIn} />);

  // Nothing of the app is reachable until a grown-up has consented. The server
  // enforces this independently; this is just so the child sees why.
  if (!profile.parentReady) {
    return scrollable(
      <WaitingForParentScreen profile={profile} onReady={refresh} onSignOut={signOut} />,
    );
  }

  /*
    The subscription has lapsed. Checked before the tour — teaching a child the
    three tabs and then telling them none of the three work is worse than not
    teaching them — and yielding to `parentMode`, because the button on this
    screen is the one that opens the grown-up's gate.

    The shell is genuinely gone rather than hidden, so clearing `resting` mounts
    three fresh screens that ask the server again. That is what makes the way
    back cost nothing: nobody has to remember to retry.
  */
  if (resting && !parentMode) {
    return scrollable(
      <RestingScreen
        profile={profile}
        message={resting}
        onGrownUps={() => setParentMode(true)}
        onSignOut={signOut}
      />,
    );
  }

  /*
    The tour, once, after consent and before the first question.

    Placed after the consent gate deliberately: a child who cannot use the app
    yet should not be taught how to, only to be handed the waiting screen at the
    end of it. Optimistic — the screen closes the moment they finish and the
    flag is written behind them, because a child should never wait on the
    network to leave a tour, and re-showing it after a failed write is a far
    smaller harm than a spinner here.
  */
  if (!profile.guideSeen && !guideDismissed) {
    return (
      <GuideScreen
        autoRead={profile.band === "early"}
        onDone={() => {
          setGuideDismissed(true);
          void api.guideSeen().catch(() => {});
        }}
      />
    );
  }

  if (parentMode) {
    return scrollable(
      <div className="mx-auto max-w-[860px] px-4">
        <DeviceScreen
          profile={profile}
          onSignOut={signOut}
          onExit={() => {
            setParentMode(false);
            wakeUp();
          }}
        />
      </div>,
    );
  }

  /*
    The document itself must not scroll.

    The tab bar and the composer are `position: fixed`, which pins them to the
    layout viewport — and on iOS the layout viewport is not where the screen is
    during a rubber-band. Overscroll past the end translates the whole visual
    viewport, dragging the "fixed" furniture with it, so a child who flicks too
    hard sees the tab bar slide off the bottom of their iPad. No amount of
    `overscroll-behavior` on the body prevents that in WKWebView.

    So the shell is exactly one viewport tall and hides its overflow, and the
    content gets its own scroller inside. With nothing left for the document to
    scroll, there is no rubber-band and the bars physically cannot move.

    Kid surface only — the parent portal returns above this and keeps ordinary
    document scrolling, which is right for a grown-up on a laptop.
  */
  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      {/*
        The scroller spans the full width; only the content inside it is
        centred and capped.

        These used to be the same element, which meant the scrollable area WAS
        the 860px column — so on an iPad held in landscape, the wide margins
        either side were dead. A child resting their thumb where a tablet is
        naturally held would swipe and watch nothing move. The column is about
        line length, which is a property of the text, not of where a finger is
        allowed to land.
      */}
      {/* data-scroller: the sky sizes itself against this box rather than
          against the window, so it fills the room it actually has. */}
      <div data-scroller className="flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto max-w-[860px]">
          <TopBar profile={profile} onGrownUps={() => setParentMode(true)} />

          {/*
            All three stay mounted; the inactive ones are hidden.

            They used to be conditionally rendered, which unmounts a screen the
            moment a child leaves it — and a question takes about ten seconds to
            answer. So a child who asked something and went to look at their sky
            while they waited came back to an empty chat: the request was still
            in flight, its answer arrived at a component that no longer existed,
            and the star it had already earned was unreachable. Waiting is
            exactly when a child wanders.

            The cost is three screens' worth of DOM and one extra fetch at
            startup. The alternative is lifting every screen's state into here,
            which is the same fix with more moving parts.
          */}
          <div className="px-4 pb-32">
            <div className={tab === "ask" ? undefined : "hidden"}>
              <AskScreen
                active={tab === "ask"}
                onAwarded={onAwarded}
                autoRead={profile.band === "early"}
              />
            </div>
            <div className={tab === "sky" ? undefined : "hidden"}>
              <SkyScreen active={tab === "sky"} onAwarded={onAwarded} />
            </div>
            <div className={tab === "goal" ? undefined : "hidden"}>
              <GoalScreen active={tab === "goal"} onAwarded={onAwarded} />
            </div>
          </div>
        </div>
      </div>

      <TabBar
        tab={tab}
        setTab={(next) => {
          setTab(next);
          refresh();
        }}
        fadingCount={profile.fadingCount}
      />

      {toast && <Toast message={toast} />}
    </div>
  );
}
